import { useEffect, useState } from "react";
import { FoldCard } from "./FoldCard";
import { fetchQuestDbView, type QuestDbView } from "../lib/questdbViewApi";

const btn =
  "rounded-md border border-border-subtle px-2 py-1 text-text-primary hover:bg-page disabled:cursor-not-allowed disabled:opacity-40";

function clock(ts: number | null | undefined, fraction = true) {
  if (ts == null || !Number.isFinite(ts)) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Amsterdam",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
    ...(fraction ? { fractionalSecondDigits: 3 } : {}),
  }).format(ts);
}

function formatCell(value: unknown) {
  if (value == null) return "null";
  return String(value);
}

function RawQueryTable({
  raw,
  maxHeightClass = "max-h-48",
}: {
  raw: { columns?: string[]; rows?: unknown[][] } | null | undefined;
  maxHeightClass?: string;
}) {
  const columns = raw?.columns ?? [];
  const rows = raw?.rows ?? [];
  if (!columns.length) {
    return (
      <p className="text-text-secondary" style={{ fontSize: "var(--font-nano)" }}>
        —
      </p>
    );
  }
  return (
    <div
      className={`mt-1 overflow-auto rounded-md border border-border-subtle bg-page ${maxHeightClass}`}
    >
      <table
        className="w-max min-w-full border-collapse font-mono text-text-primary"
        style={{ fontSize: "var(--font-nano)", fontVariantNumeric: "tabular-nums" }}
      >
        <thead className="sticky top-0 z-[1] bg-page shadow-[0_1px_0_var(--color-border-subtle)]">
          <tr>
            {columns.map((col) => (
              <th
                key={col}
                className="whitespace-nowrap px-2 py-1.5 text-left font-semibold text-text-secondary"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-2 py-2 text-text-secondary"
              >
                No rows
              </td>
            </tr>
          ) : (
            rows.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className="border-t border-border-subtle/60 even:bg-page/50"
              >
                {columns.map((col, colIndex) => (
                  <td key={`${rowIndex}-${col}`} className="whitespace-nowrap px-2 py-1 align-top">
                    {formatCell(Array.isArray(row) ? row[colIndex] : null)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function age(ms: number | null | undefined) {
  if (ms == null || !Number.isFinite(ms)) return "—";
  if (ms < 0) return "0 ms ago";
  if (ms < 10_000) return `${Math.round(ms)} ms ago`;
  return `${Math.round(ms / 1000)} s ago`;
}

const ADMIN_ONLY_TITLE = "Admin only";

export function QuestDbViewCard({ canControl = true }: { canControl?: boolean }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<QuestDbView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    setBusy(true);
    setError(null);
    try {
      setView(await fetchQuestDbView());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (open && !view && !busy) {
      void refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once when section opens
  }, [open]);

  const freq = view?.freq;
  const samples = view?.samples;
  const freqOk = Boolean(freq && !freq.error && (freq.rows ?? 0) > 0);
  const sampleOk = Boolean(samples && !samples.error && (samples.seconds_with_rows ?? 0) > 0);
  const summary = !view
    ? "Refresh to read QuestDB. No automatic read."
    : freqOk && sampleOk
      ? `freq ${freq?.rows} rows / ${freq?.window_s}s · samples ${samples?.chargers_min === samples?.chargers_max ? samples?.chargers_min : `${samples?.chargers_min}–${samples?.chargers_max}`} chargers · read ${clock(view.read_at_ms)}`
      : `read ${clock(view.read_at_ms)}`;

  return (
    <FoldCard
      title="QuestDB"
      open={open}
      onToggle={() => setOpen((value) => !value)}
      summary={summary}
      actions={
        <button
          type="button"
          className={btn}
          style={{ fontSize: "var(--font-micro)" }}
          disabled={!canControl || busy}
          title={!canControl ? ADMIN_ONLY_TITLE : undefined}
          onClick={() => void refresh()}
        >
          {busy ? "Reading…" : "Refresh"}
        </button>
      }
    >
      {error && (
        <p className="text-status-error" style={{ fontSize: "var(--font-body)" }}>
          {error}
        </p>
      )}
      {!view && !error && (
        <p className="text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
          One read of the last 60 seconds when you press Refresh.
        </p>
      )}
      {freq && (
        <section className="mb-4">
          <p className="font-medium text-text-primary" style={{ fontSize: "var(--font-body)" }}>
            Frequencies · {freq.table}
          </p>
          {freq.error ? (
            <p className="text-status-error" style={{ fontSize: "var(--font-micro)" }}>
              {freq.error}
            </p>
          ) : (
            <>
              <p className="text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
                Latest {clock(freq.latest_ts_ms)} · {freq.latest_hz ?? "—"} Hz · {freq.source || "—"} · {age(freq.age_ms)}
              </p>
              <p className="text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
                {freq.rows} rows in {freq.window_s}s · median spacing {freq.median_interval_ms ?? "—"} ms · {freq.gaps} gaps of {freq.gap_over_ms} ms or more
              </p>
              <p className="mt-2 text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
                Latest 2 rows
              </p>
              <RawQueryTable raw={freq.raw} maxHeightClass="max-h-40" />
            </>
          )}
        </section>
      )}
      {samples && (
        <section>
          <p className="font-medium text-text-primary" style={{ fontSize: "var(--font-body)" }}>
            Measurements · {samples.table}
          </p>
          {samples.error ? (
            <p className="text-status-error" style={{ fontSize: "var(--font-micro)" }}>
              {samples.error}
            </p>
          ) : (
            <>
              <p className="text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
                Latest second {clock(samples.latest_ts_ms)} · {age(samples.age_ms)}
              </p>
              <p className="text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
                {samples.seconds_with_rows} seconds with rows in a {samples.span_s}s span · {samples.missing_seconds} seconds with no rows
                {samples.chargers_min != null
                  ? ` · ${samples.chargers_min === samples.chargers_max ? `${samples.chargers_min} chargers every second` : `${samples.chargers_min}–${samples.chargers_max} chargers`}`
                  : ""}
                {samples.duplicate_seconds ? ` · ${samples.duplicate_seconds} seconds with a repeated charger` : " · one row per charger"}
              </p>
              <p className="text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
                measurement_kw {samples.measurement_min ?? "—"} … {samples.measurement_max ?? "—"} · baseline_kw filled {samples.baseline_filled ?? 0}
              </p>
              {(samples.missing_ts_ms ?? []).length > 0 && (
                <p className="text-text-secondary" style={{ fontSize: "var(--font-nano)" }}>
                  Seconds with no charger rows: {samples.missing_ts_ms?.map((ts) => clock(ts, false)).join(", ")}. The writer stamps the wall clock, then waits the rest of 1.0 s, so a late tick can skip a civil second. The seconds on either side still have a full batch.
                </p>
              )}
              <pre className="mt-2 max-h-64 overflow-auto rounded-md border border-border-subtle bg-page p-2 text-text-primary" style={{ fontSize: "var(--font-nano)" }}>
                {(samples.seconds ?? [])
                  .map((row) => `${clock(row.ts_ms, false)}  rows=${row.rows}  chargers=${row.chargers}  kW ${row.measurement_min}…${row.measurement_max}`)
                  .join("\n") || "—"}
              </pre>
              <p className="mt-2 text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
                Latest 4 rows with non-null values
              </p>
              <RawQueryTable raw={samples.raw_non_null} />
              <p className="mt-2 text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
                power &gt; 0
              </p>
              <RawQueryTable raw={samples.raw_power} />
            </>
          )}
        </section>
      )}
    </FoldCard>
  );
}
